// @flow

import { createAction, createReducer } from 'redux-act';
import { createSelector } from 'reselect';

import { GET } from 'gandi.redux-fetcher';
import {
  selectCurrentId,
  selectFilteredOrganizations,
  selectPersonalId,
  type Organizations,
} from '@gandi/redux-sharing';

import type { Reducer } from 'redux';
import type {
  Dispatch,
  GetState,
  FullState,
  FetchResponse,
  SupportState,
} from '../../types';

export const fetchFail: any = createAction('receive organization fail');
export const fetchStart: any = createAction('fetch organization');
export const fetchSuccess: any = createAction('receive organization');

// Selectors
const selectRaw = (state: FullState) => state.support.organization;

// TODO: Replace any to be more accurate
const selectRawData: (FullState, mixed) => any = createSelector(
  selectRaw,
  organization => organization.data || {}
);

export const selectSharingOrgaDetails: (FullState, mixed) => any =
  createSelector(selectRawData, data => data.sharing || {});

export const selectPersonalOrgaDetails: (FullState, void) => any =
  createSelector(selectRawData, data => data.personal || {});

export const selectOrganizations = (state: FullState): Organizations =>
  selectFilteredOrganizations(state) || [];

export const selectOrganizationsOptions: FullState => Array<{|
  label: string,
  value: string,
|}> = createSelector(selectOrganizations, organizations =>
  organizations.map(organization => ({
    label: organization.name,
    value: organization.id,
  }))
);

// Actions
export function fetchCurrentOrganization(): (
  dispatch: Dispatch,
  getState: GetState
) => Promise<void> {
  const kind = 'sharing';
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    dispatch(fetchStart());

    const id = selectCurrentId(state);
    if (!id) {
      // Not authenticated.
      return Promise.resolve();
    }

    // Don't refetch if it's the same id as the personal organization.
    const persoDetails = selectPersonalOrgaDetails(state);
    if (persoDetails && id === persoDetails.id) {
      return dispatch(fetchSuccess({ data: persoDetails, kind }));
    }

    return dispatch(
      GET(`/organization/organizations/${id}`, {
        on2XX: (response: FetchResponse<200, { [string]: mixed }>) =>
          dispatch(fetchSuccess({ data: response.data, kind })),
        no2XX: () => dispatch(fetchFail({ kind })),
      })
    );
  };
}

export function fetchPersonalOrganization(): (
  dispatch: Dispatch,
  getState: GetState
) => Promise<void> {
  const kind = 'personal';
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    dispatch(fetchStart());

    const id = selectPersonalId(state);
    if (!id) {
      // Not authenticated.
      return Promise.resolve();
    }

    // Don't refetch if it's the same id as the current sharing id.
    const sharingDetails = selectPersonalOrgaDetails(state);
    if (sharingDetails && id === sharingDetails.id) {
      return dispatch(fetchSuccess({ data: sharingDetails, kind }));
    }

    return dispatch(
      GET(`/organization/organizations/${id}`, {
        on2XX: (response: FetchResponse<200, { [string]: mixed }>) =>
          dispatch(fetchSuccess({ data: response.data, kind })),
        no2XX: () => dispatch(fetchFail({ kind })),
      })
    );
  };
}

// Reducer
const initialState: SupportState['organization'] = {
  fetching: false,
  data: null,
};
const fetchFailHandler = (
  state: SupportState['organization'],
  payload: any
) => ({
  ...state,
  data: {
    [payload.kind]: null,
  },
  fetching: false,
});
const fetchStartHandler = (state: SupportState['organization']) => ({
  ...state,
  fetching: true,
});
const fetchSuccessHandler = (
  state: SupportState['organization'],
  payload: any
) => ({
  ...state,
  data: {
    ...state.data,
    [payload.kind]: payload.data,
  },
  fetching: false,
});

// $FlowFixMe[incompatible-exact]
const reducer: Reducer<SupportState['organization'], mixed> = createReducer(
  {
    [fetchFail]: fetchFailHandler,
    [fetchStart]: fetchStartHandler,
    [fetchSuccess]: fetchSuccessHandler,
  },
  initialState
);
export default reducer;
